import { Ear, Trash2 } from 'lucide-react';

import { Section } from '@/components/ui/Section';
import type { RevealStep } from '@/lib/schema.types';
import { useEditorActions } from '@/store/editor.store';

interface Props {
  slideId: string;
  step: RevealStep | null;
}

/**
 * Per-step inspector: pause-for-input toggle plus an inventory of the step's
 * annotations and freehand strokes (with remove). Writes via upsertRevealStep.
 */
export function StepInspector({ slideId, step }: Props) {
  const actions = useEditorActions();

  if (!step) {
    return (
      <div className="p-4 text-xs text-fg-subtle">Select a reveal step to inspect it.</div>
    );
  }

  const annotations = step.annotations ?? [];
  const strokes = step.drawStrokes ?? [];

  const patch = (next: Partial<RevealStep>) =>
    actions.upsertRevealStep(slideId, { ...step, ...next });

  return (
    <div className="space-y-1">
      <Section title="Interaction">
        <label className="flex items-center gap-2 text-sm text-fg">
          <input
            type="checkbox"
            checked={!!step.takeInput}
            onChange={(e) => patch({ takeInput: e.target.checked || undefined })}
          />
          <Ear size={14} className="text-fg-muted" />
          Pause &amp; listen for input after narration
        </label>
        <p className="mt-1 text-[10.5px] text-fg-subtle">
          The classroom waits for a student response (e.g. an MCQ answer) before
          advancing.
        </p>
      </Section>

      <Section title={`Annotations (${annotations.length})`}>
        {annotations.length === 0 ? (
          <p className="text-[11px] text-fg-subtle">
            None. Pick a tool above and click an element to add one.
          </p>
        ) : (
          <ul className="space-y-1">
            {annotations.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-[12px]"
              >
                <span
                  className="h-3 w-3 rounded-sm"
                  style={{ background: a.color ?? '#facc15' }}
                />
                <span className="flex-1 capitalize">{a.kind}</span>
                <button
                  onClick={() => patch({ annotations: annotations.filter((x) => x.id !== a.id) })}
                  className="text-fg-subtle hover:text-red-500"
                  title="Remove annotation"
                >
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={`Drawings (${strokes.length})`}>
        {strokes.length === 0 ? (
          <p className="text-[11px] text-fg-subtle">
            None. Use the Pen or Highlighter tool to draw on the slide.
          </p>
        ) : (
          <ul className="space-y-1">
            {strokes.map((s) => (
              <li
                key={s.id}
                className="flex items-center gap-2 rounded-md border border-border px-2 py-1 text-[12px]"
              >
                <span className="h-3 w-3 rounded-full" style={{ background: s.color }} />
                <span className="flex-1">{s.points.length} pts</span>
                <button
                  onClick={() => patch({ drawStrokes: strokes.filter((x) => x.id !== s.id) })}
                  className="text-fg-subtle hover:text-red-500"
                  title="Remove stroke"
                >
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
